import React, { useRef } from 'react';
import { Camera } from 'lucide-react';

/**
 * QuickCaptureModal
 *
 * Full-screen overlay presented when the app is launched via the
 * `?quick-capture=receipt` PWA shortcut. Gives the user a single large
 * camera-first tap target to photograph or choose a receipt file.
 *
 * Props:
 *   open           {boolean}  — controlled open state
 *   onClose        {function} — () => void   — called on Cancel
 *   onFileSelected {function} — (file: File) => void — called when user picks a file
 */
export default function QuickCaptureModal({ open, onClose, onFileSelected }) {
    const inputRef = useRef(null);

    if (!open) return null;

    const handleCardClick = () => {
        inputRef.current?.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file && onFileSelected) {
            onFileSelected(file);
        }
        // Reset so the same file can be re-selected if the user cancels and retries
        e.target.value = '';
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center gap-6 p-6"
            role="dialog"
            aria-modal="true"
            aria-label="Capture receipt"
        >
            {/* App name heading */}
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Hisland Time
            </p>

            {/* Large camera card */}
            <button
                type="button"
                onClick={handleCardClick}
                className="w-full max-w-sm flex flex-col items-center justify-center gap-4 rounded-lg border border-border bg-muted/40 p-10 hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                aria-label="Take photo or choose a file"
            >
                <Camera className="w-12 h-12 text-foreground" aria-hidden="true" />
                <div className="text-center">
                    <p className="font-semibold text-base uppercase tracking-widest">Take Photo</p>
                    <p className="text-sm text-muted-foreground mt-1">or tap to choose a file</p>
                </div>
            </button>

            {/* Hidden file input — camera-first on mobile */}
            <input
                ref={inputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/heic,image/heif,image/webp"
                capture="environment"
                onChange={handleFileChange}
                className="sr-only"
                aria-hidden="true"
                tabIndex={-1}
            />

            {/* Cancel */}
            <button
                type="button"
                onClick={onClose}
                className="text-sm text-muted-foreground underline hover:no-underline inline-flex items-center min-h-[44px] px-2"
            >
                Cancel
            </button>
        </div>
    );
}
